import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Dashboard from "@/pages/Dashboard";
import SalesOrders from "@/pages/SalesOrders";
import ProductionPlanningReport from "@/pages/ProductionPlanningReport";
import PlanningAgent from "@/pages/PlanningAgent";
import Scheduling from "@/pages/Scheduling";
import Forecasting from "@/pages/Forecasting";
import ProductionOrderTracker from "@/pages/ProductionOrderTracker";

const PlanningAnalyst = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("materials");
  const requestedTab = new URLSearchParams(location.search).get("tab");

  useEffect(() => {
    if (["materials", "sales-orders", "planning-report", "ai-agent", "scheduling", "forecasting", "po-tracker"].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  return (
    <div className="space-y-4" data-testid="planning-analyst-page">
      {/* Hero Header Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-blue-100/80 bg-gradient-to-r from-blue-50/80 via-blue-50/40 to-slate-50/20 p-5 shadow-xs transition-all">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-200 shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 heading-display">PRODUCTION PLANNING</h1>
              <p className="text-sm font-normal text-slate-500 mt-0.5">
                Materials management, scheduling, forecasting, and AI-powered production planning
              </p>
            </div>
          </div>

          {/* Right side Tagline */}
          <div className="hidden md:flex flex-col items-end text-right shrink-0">
            <span className="text-sm font-bold text-slate-700 tracking-wide">Plan Today</span>
            <span className="text-sm font-bold text-blue-600 tracking-wide">Produce Tomorrow</span>
            <div className="w-7 h-0.5 bg-blue-500 rounded-full mt-1" />
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
        <TabsList className="w-full justify-start flex-wrap h-auto gap-1 p-1 bg-slate-100/70 border border-slate-200/60 rounded-xl" data-testid="planning-tabs">
          <TabsTrigger value="materials" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="sales-orders" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-sales-orders">Sales Orders</TabsTrigger>
          <TabsTrigger value="planning-report" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-report">Planning Report</TabsTrigger>
          <TabsTrigger value="ai-agent" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-ai-agent">AI Agent</TabsTrigger>
          <TabsTrigger value="scheduling" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-scheduling">Scheduling</TabsTrigger>
          <TabsTrigger value="forecasting" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-forecasting">Forecast</TabsTrigger>
          <TabsTrigger value="po-tracker" className="rounded-lg text-sm font-medium px-3.5 py-1.5 data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-xs" data-testid="planning-tab-po-tracker">PO Tracker</TabsTrigger>
        </TabsList>

        <TabsContent value="materials" className="mt-4">
          <Dashboard />
        </TabsContent>

        <TabsContent value="sales-orders" className="mt-4">
          <SalesOrders />
        </TabsContent>

        <TabsContent value="planning-report" className="mt-4">
          <ProductionPlanningReport />
        </TabsContent>

        <TabsContent value="ai-agent" className="mt-4">
          <PlanningAgent />
        </TabsContent>

        <TabsContent value="scheduling" className="mt-4">
          <Scheduling />
        </TabsContent>

        <TabsContent value="forecasting" className="mt-4">
          <Forecasting />
        </TabsContent>

        <TabsContent value="po-tracker" className="mt-4">
          <ProductionOrderTracker />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PlanningAnalyst;
